import { useEffect, useMemo, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export interface PublishAccountOption {
  id: string;
  name: string;
  email: string | null;
  avatarUrl: string | null;
  isTenant: boolean;
}

export interface PublishAccountState {
  loading: boolean;
  /** Connected YouTube accounts the user can publish to. */
  accounts: PublishAccountOption[];
  /** The project's default account, when it is still a usable account. */
  defaultId: string | null;
  selectedId: string | null;
  selected: PublishAccountOption | null;
  setSelectedId: (id: string | null) => void;
  /** Several accounts and none chosen — publishing must not silently pick one. */
  needsChoice: boolean;
}

/**
 * The YouTube account a post will be published to. Starts on the project's default account (or
 * the only account); the user can change it. Pass no projectId to skip loading.
 */
export function usePublishAccount(projectId?: string | null): PublishAccountState {
  const [loading, setLoading] = useState(Boolean(projectId));
  const [accounts, setAccounts] = useState<PublishAccountOption[]>([]);
  const [rawDefaultId, setRawDefaultId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setSelectedId(null);
    Promise.all([
      fetch(`${API_BASE}/api/accounts`, { credentials: 'include' }).then(r => (r.ok ? r.json() : { accounts: [] })),
      fetch(`${API_BASE}/api/projects/${projectId}`, { credentials: 'include' }).then(r => (r.ok ? r.json() : null)),
    ])
      .then(([accountData, project]) => {
        if (cancelled) return;
        const options: PublishAccountOption[] = (accountData.accounts || [])
          .filter((a: any) => a.provider === 'youtube' && (a.status === 'connected' || a.status === 'syncing'))
          .map((a: any) => ({
            id: a.id,
            name: String(a.profileData?.displayName || a.providerEmail || 'YouTube account'),
            email: a.providerEmail || null,
            avatarUrl: a.profileData?.avatarUrl || null,
            isTenant: a.linkRole === 'tenant',
          }));
        const defaultId: string | null = project?.default_linked_account_id || null;
        setAccounts(options);
        setRawDefaultId(defaultId);
        setSelectedId(prev => {
          if (prev && options.some(o => o.id === prev)) return prev;
          if (defaultId && options.some(o => o.id === defaultId)) return defaultId;
          return options.length === 1 ? options[0].id : null;
        });
      })
      .catch(() => { /* leave empty — publishing falls back to the server's rules */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  return useMemo(() => {
    const defaultId = rawDefaultId && accounts.some(a => a.id === rawDefaultId) ? rawDefaultId : null;
    const selected = accounts.find(a => a.id === selectedId) || null;
    return {
      loading,
      accounts,
      defaultId,
      selectedId: selected ? selected.id : null,
      selected,
      setSelectedId,
      needsChoice: accounts.length > 1 && !selected,
    };
  }, [loading, accounts, rawDefaultId, selectedId]);
}
